import {
    collection,
    doc,
    addDoc,
    setDoc,
    getDoc,
    getDocs,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    limit,
    writeBatch,
    getCountFromServer
} from "firebase/firestore";
import { db } from "./config";

export const addDocument = async (collectionName, data) => {
    try {
        const docRef = await addDoc(collection(db, collectionName), {
            ...data,
            createdAt: new Date(),
            updatedAt: new Date()
        });
        return docRef.id;
    } catch (error) {
        console.error("Error adding document:", error);
        throw error;
    }
};

export const setDocument = async (collectionName, docId, data, merge = false) => {
    try {
        const docData = {
            ...data,
            updatedAt: new Date()
        };
        if (!merge) {
            docData.createdAt = new Date();
        }
        await setDoc(doc(db, collectionName, docId), docData, { merge });
    } catch (error) {
        console.error("Error setting document:", error);
        throw error;
    }
};

export const getDocumentById = async (collectionName, docId) => {
    try {
        const docSnap = await getDoc(doc(db, collectionName, docId));
        if (docSnap.exists()) {
            return {
                id: docSnap.id,
                ...docSnap.data()
            };
        }
        return null;
    } catch (error) {
        console.error("Error getting document:", error);
        throw error;
    }
};

export const getCollectionDocuments = async (collectionName) => {
    try {
        const querySnapshot = await getDocs(collection(db, collectionName));
        return querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error("Error getting collection:", error);
        throw error;
    }
};

export const queryDocuments = async (collectionName, conditions = [], orderByField = null, limitCount = null) => {
    try {
        let constraints = [];

        if (conditions.length > 0) {
            constraints = conditions.map(([field, op, value]) =>
                where(field, op, value)
            );
        }

        if (orderByField) {
            constraints.push(orderBy(orderByField.field, orderByField.direction || "asc"));
        }

        if (limitCount) {
            constraints.push(limit(limitCount));
        }

        const q = query(collection(db, collectionName), ...constraints);
        const querySnapshot = await getDocs(q);

        return querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error("Error querying documents:", error);
        throw error;
    }
};

export const updateDocument = async (collectionName, docId, updates) => {
    try {
        await updateDoc(doc(db, collectionName, docId), {
            ...updates,
            updatedAt: new Date()
        });
    } catch (error) {
        console.error("Error updating document:", error);
        throw error;
    }
};

export const deleteDocument = async (collectionName, docId) => {
    try {
        await deleteDoc(doc(db, collectionName, docId));
    } catch (error) {
        console.error("Error deleting document:", error);
        throw error;
    }
};

export const batchUpdate = async (updates) => {
    try {
        const batch = writeBatch(db);

        updates.forEach(({ collectionName, docId, data }) => {
            const docRef = doc(db, collectionName, docId);
            batch.set(docRef, { ...data, updatedAt: new Date() }, { merge: true });
        });

        await batch.commit();
    } catch (error) {
        console.error("Error in batch update:", error);
        throw error;
    }
};

export const getCollectionCount = async (collectionName) => {
    try {
        const coll = collection(db, collectionName);
        const snapshot = await getCountFromServer(coll);
        return snapshot.data().count;
    } catch (error) {
        console.error("Error getting collection count:", error);
        throw error;
    }
};

export default db;
